import type { FunctionComponent } from 'react';

import { useDesktop } from '../../hooks/breakpoint';
import { useInitialElementDimensions } from '../../hooks/use-element-dimensions';
import { useImageCDN } from '../../hooks/use-image-cdn';
import { createClassName, createStyleHelper } from '../../utils/class-names';
import { ButtonSize } from '../button/button';
import { ButtonCta } from '../button-cta/button-cta';
import type { IBannerCollection } from '../carousel/carousel.interface';

import { SlideType } from './slide.interface';
import styles from './slide.module.scss';

const style = createStyleHelper(styles, 'slide');

export interface ISlideProps {
    className?: string;
    slide: IBannerCollection['banners'][0];
    type?: SlideType;
}

export const Slide: FunctionComponent<ISlideProps> = ({
    className,
    slide,
    type = SlideType.LOBBY,
}) => {
    const isFullDisplay = useDesktop();
    const assets = slide.assets;
    const { measurementRef, width } = useInitialElementDimensions();
    const maxWidth = isFullDisplay ? width / 2 : width;
    const background = isFullDisplay ? assets.largeBackground : assets.smallBackground;
    const midground = isFullDisplay ? assets.largeMidground : assets.smallMidground;
    const backgroundCdn = useImageCDN(background, maxWidth);
    const midgroundCdn = useImageCDN(midground, maxWidth);
    const logoCdn = useImageCDN(slide.assets.additionalLogo, maxWidth);

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('slide', [type])}>
                <div ref={measurementRef} className={style('imagery', [type])}>
                    {backgroundCdn ? (
                        <img className={style('background-image', [type])} src={backgroundCdn} />
                    ) : null}

                    {midgroundCdn ? (
                        <img className={style('top-image')} src={midgroundCdn} />
                    ) : null}

                    {logoCdn ? <img className={style('logo')} src={logoCdn} /> : null}
                </div>

                {slide.content.shortHeading || slide.content.shortSubheading ? (
                    <aside className={style('promo', [type])}>
                        {slide.callToActions?.length > 0 && (
                            <ButtonCta
                                className={style('cta', [type])}
                                size={ButtonSize.LARGE}
                                callToAction={slide.callToActions[0]}
                                hollow={true}
                             />
                        )}
                        <h5 className={style('title', [type])}>{slide.content.shortHeading}</h5>
                        <p className={style('description', [type])}>
                            {slide.content.shortSubheading}
                        </p>
                    </aside>
                ) : null}
            </div>
        </div>
    );
};
